import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Eye, EyeOff, Loader2, Upload } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfile, Profile } from "@/hooks/useProfile";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PasswordStrengthMeter } from "@/components/PasswordStrengthMeter";

// Helper to obscure account ID
const obscureId = (id: string): string => {
  if (!id || id.length < 8) return id;
  const firstFour = id.substring(0, 4);
  const lastFour = id.substring(id.length - 4);
  return `${firstFour}...${lastFour}`;
};

const Settings = () => {
  const { user, updatePassword } = useAuth();
  const { toast } = useToast();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { 
    profile, 
    isLoading: profileLoading, 
    error: profileError,
    updateProfile,
    uploadAvatar,
    isUploading
  } = useProfile();
  
  // Check password strength
  useEffect(() => {
    if (!newPassword) {
      setPasswordError(null);
      return;
    }
    
    // Reset error if passwords match when both are entered
    if (confirmPassword && newPassword === confirmPassword) {
      setPasswordError(null);
    }
    
    // Check password strength
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
    } else if (!/[A-Z]/.test(newPassword)) {
      setPasswordError("Password must contain at least one uppercase letter");
    } else if (!/[a-z]/.test(newPassword)) {
      setPasswordError("Password must contain at least one lowercase letter");
    } else if (!/[0-9]/.test(newPassword)) {
      setPasswordError("Password must contain at least one number");
    } else if (!/[^A-Za-z0-9]/.test(newPassword)) {
      setPasswordError("Password must contain at least one special character");
    } else {
      setPasswordError(null);
    }
  }, [newPassword, confirmPassword]);
  
  // Handle profile form submission
  const handleProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Create object with proper default values for required fields
    const updatedProfile = {
      first_name: formData.get('first_name') as string || null,
      last_name: formData.get('last_name') as string || null,
      company: formData.get('company') as string || null,
      phone: formData.get('phone') as string || null,
      website: formData.get('website') as string || null,
    };
    
    console.log("Updating profile with:", updatedProfile);
    updateProfile(updatedProfile);
  };
  
  // Handle notification settings update
  const handleNotificationUpdate = (key: 'email_notifications' | 'project_updates' | 'marketing_emails', value: boolean) => {
    if (!profile) return;
    
    updateProfile({
      [key]: value
    });
  };
  
  // Handle avatar upload
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    await uploadAvatar(files[0]);
  };
  
  // Handle password update
  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Basic validation
      if (!newPassword) {
        toast({
          title: "Error",
          description: "Please enter a new password",
          variant: "destructive",
        });
        return;
      }
      
      if (passwordError) {
        toast({
          title: "Error",
          description: passwordError,
          variant: "destructive",
        });
        return;
      }
      
      if (newPassword !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }
      
      await updatePassword(newPassword);
      setNewPassword("");
      setConfirmPassword("");
      
    } catch (error) {
      // Error is handled in the updatePassword function
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show loading state while profile data is being fetched
  if (profileLoading) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Show error state if profile data couldn't be fetched
  if (profileError) {
    return (
      <div className="p-8 max-w-6xl mx-auto space-y-6">
        <div className="space-y-0.5">
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground">
            Manage your account and preferences
          </p>
        </div>
        
        <div className="bg-destructive/15 p-4 rounded-md text-destructive">
          <h3 className="font-semibold">Error loading profile</h3>
          <p className="text-sm">Could not load profile data. Please refresh the page.</p>
          <Button variant="outline" className="mt-2" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="space-y-0.5">
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account and preferences
        </p>
      </div>

      <Tabs defaultValue="account" className="space-y-4">
        <TabsList>
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="account" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>
                Your basic account information and email address.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={user?.email || ""} disabled />
                  <p className="text-xs text-muted-foreground">Your email address cannot be changed</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account-id">Account ID</Label>
                  <Input id="account-id" value={user?.id ? obscureId(user.id) : ""} disabled />
                  <p className="text-xs text-muted-foreground">Your unique account identifier</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Password</CardTitle>
              <CardDescription>
                Change your password to keep your account secure.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handlePasswordUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input 
                      id="new-password" 
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pr-10"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input 
                    id="confirm-password" 
                    type="password" 
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                
                {newPassword && (
                  <div className="space-y-2">
                    <Label>Password Strength</Label>
                    <PasswordStrengthMeter password={newPassword} error={passwordError} />
                  </div>
                )}
                
                <Button 
                  type="submit" 
                  disabled={!newPassword || !!passwordError || newPassword !== confirmPassword || isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="profile" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Profile Picture</CardTitle>
              <CardDescription>
                Update your profile photo. This will be displayed throughout the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={profile?.avatar_url || ""} alt="Profile picture" />
                  <AvatarFallback>
                    {profile?.first_name?.[0] || ""}{profile?.last_name?.[0] || ""}
                  </AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <Label htmlFor="avatar" className="cursor-pointer">
                    <div className="flex items-center gap-2 p-2 border rounded-md hover:bg-accent">
                      <Upload className="h-4 w-4" />
                      <span>{isUploading ? "Uploading..." : "Upload new picture"}</span>
                    </div>
                    <Input 
                      id="avatar" 
                      type="file" 
                      accept="image/jpeg, image/png, image/webp, image/gif" 
                      className="hidden" 
                      onChange={handleAvatarUpload}
                      disabled={isUploading}
                    />
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Maximum size: 5MB. Accepted formats: JPEG, PNG, WebP, GIF.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your profile details and personal information.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input 
                      id="first_name" 
                      name="first_name" 
                      defaultValue={profile?.first_name || ""}
                      maxLength={100}
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input 
                      id="last_name" 
                      name="last_name" 
                      defaultValue={profile?.last_name || ""}
                      maxLength={100}
                      placeholder="Your last name"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input 
                    id="company" 
                    name="company" 
                    defaultValue={profile?.company || ""}
                    placeholder="Your company or organization"
                    maxLength={100}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      defaultValue={profile?.phone || ""}
                      placeholder="+1 (555) 123-4567"
                      maxLength={20}
                      pattern="[0-9\s()+\-]+"
                      title="Phone number can only contain digits, spaces, parentheses, plus sign, and hyphens"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website</Label>
                    <Input 
                      id="website" 
                      name="website" 
                      defaultValue={profile?.website || ""}
                      placeholder="https://example.com"
                      maxLength={200}
                      type="text"
                    />
                  </div>
                </div>
                
                <Button type="submit">
                  Save Changes
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Choose what notifications you want to receive.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive email updates about your content generation.
                  </p>
                </div>
                <Switch 
                  checked={profile?.email_notifications || false}
                  onCheckedChange={(checked) => handleNotificationUpdate('email_notifications', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Project Updates</Label>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your projects are completed.
                  </p>
                </div>
                <Switch 
                  checked={profile?.project_updates || false}
                  onCheckedChange={(checked) => handleNotificationUpdate('project_updates', checked)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Marketing Emails</Label>
                  <p className="text-sm text-muted-foreground">
                    Receive product updates and marketing communications.
                  </p>
                </div>
                <Switch 
                  checked={profile?.marketing_emails || false}
                  onCheckedChange={(checked) => handleNotificationUpdate('marketing_emails', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
